<template>
	<div class="time-picker-control">
		<div class="control-wheels">
			<div
				ref="minutes"
				:style="minutesWheelStyle"
				class="minutes"
				@scroll="handleMinutesScroll"
			>
				<div
					v-for="option in minuteOptions"
					:key="`minute-option-${option.key}`"
					class="option"
				>
					{{ option.value.toString().padStart(2, '0') }}
				</div>
			</div>
			<div
				ref="seconds"
				:style="secondsWheelStyle"
				class="seconds"
				@scroll="handleSecondsScroll"
			>
				<div
					v-for="option in secondOptions"
					:key="`second-option-${option.key}`"
					class="option"
				>
					{{ option.value.toString().padStart(2, '0') }}
				</div>
			</div>
		</div>
		<div class="overlay">
			<div class="shadow-top" />
			<div class="separator">
				:
			</div>
			<div class="shadow-bottom" />
		</div>
	</div>
</template>

<script>

const CONTROL_ITEM_HEIGHT = 50;
const CONTROL_ITEM_COUNT = 5;
const CONTROL_HEIGHT = CONTROL_ITEM_COUNT * CONTROL_ITEM_HEIGHT;
const UPDATE_TIMEOUT = 200;

export default {
	name:  'MobileTimePickerControl',
	props: {
		value: {
			type:     Object,
			required: true,
		},
		maxMinutes: {
			// inclusive
			type:    Number,
			default: 120,
		},
	},
	data() {
		return {
			minutes:            0,
			seconds:            0,
			updateTimer:        -1,
			minuteOptionStyles: {},
		};
	},
	computed: {
		minuteOptions() {
			const options = [];
			for (let i = 0; i <= this.maxMinutes; i++) {
				options.push({
					key:   `minute-option-${i}`,
					value: i,
				});
			}
			return options;
		},
		secondOptions() {
			const options = [];
			for (let i = 0; i < 60; i++) {
				options.push({
					key:   `second-option-${i}`,
					value: i,
				});
			}
			return options;
		},
		minutesWheelStyle() {
			const verticalPadding = Math.floor(CONTROL_ITEM_COUNT / 2) * CONTROL_ITEM_HEIGHT;
			return {
				paddingTop:    `${verticalPadding}px`,
				paddingBottom: `${verticalPadding}px`,
			};
		},
		secondsWheelStyle() {
			const verticalPadding = Math.floor(CONTROL_ITEM_COUNT / 2) * CONTROL_ITEM_HEIGHT;
			return {
				paddingTop:    `${verticalPadding}px`,
				paddingBottom: `${verticalPadding}px`,
			};
		},
	},
	mounted() {
		const { minutes = 0, seconds = 0 } = this.value;
		this.minutes = minutes;
		this.seconds = seconds;
		this.updateTimeInput();
	},
	destroyed() {
		clearTimeout(this.updateTimer);
	},
	methods: {
		updateTimeInput() {
			this.$nextTick(() => {
				const minutesScroll = this.minutes * CONTROL_ITEM_HEIGHT;
				this.$refs.minutes.scroll({ top: minutesScroll });
				const secondsScroll = this.seconds * CONTROL_ITEM_HEIGHT;
				this.$refs.seconds.scroll({ top: secondsScroll });
			});
		},
		getWheelValueIndex(ref) {
			const scroll = ref.scrollTop;
			const screenIndex = Math.floor(scroll / CONTROL_HEIGHT);
			const currentItemScreenIndex = Math.round((scroll % CONTROL_HEIGHT) / CONTROL_ITEM_HEIGHT);
			const itemIndex = screenIndex * CONTROL_ITEM_COUNT + currentItemScreenIndex;
			return itemIndex;
		},
		handleMinutesScroll() {
			clearTimeout(this.updateTimer);
			this.updateTimer = setTimeout(() => {
				this.minutes = this.getWheelValueIndex(this.$refs.minutes);
				this.emitUpdates();
			}, UPDATE_TIMEOUT);
		},
		handleSecondsScroll() {
			clearTimeout(this.updateTimer);
			this.updateTimer = setTimeout(() => {
				this.seconds = this.getWheelValueIndex(this.$refs.seconds);
				this.emitUpdates();
			}, UPDATE_TIMEOUT);
		},
		emitUpdates() {
			this.$emit('input', { minutes: this.minutes, seconds: this.seconds });
		},
	},
};

</script>

<style lang="scss" scoped>
$control-item-height: 50px;
$control-visible-item-count: 5;

.time-picker-control {
	position: relative;
	font-size: 24px;

	.control-wheels {
		display: flex;
		align-items: center;
		width: 100%;
		height: $control-visible-item-count * $control-item-height;
	}

	.minutes,
	.seconds {
		flex: 1;
		text-align: center;
		height: 100%;
		overflow: auto;
		scroll-snap-type: mandatory;
		scroll-snap-type: y mandatory;
		scroll-snap-points-y: repeat($control-item-height);
		ms-overflow-style: none;
		scrollbar-width: none;

		&::-webkit-scrollbar {
			display: none;
		}

		.option {
			display: flex;
			align-items: center;
			justify-content: center;
			height: $control-item-height;
			scroll-snap-align: center;
		}
	}

	.overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 0 16px;
		pointer-events: none;
		line-height: 0;

		.shadow-top,
		.shadow-bottom {
			flex: 1;
			width: 100%;
		}
		.shadow-top {
			background: linear-gradient(180deg, var(--color-grey-900) 0%, rgba(var(--color-rgb-grey-850), 0) 100%);
		}
		.shadow-bottom {
			background: linear-gradient(180deg, var(--color-grey-900) 0%, rgba(var(--color-rgb-grey-850), 0) 100%);
			transform: rotate(-180deg);
		}

		.separator {
			flex: 0 0 1;
			display: flex;
			align-items: center;
			justify-content: center;
			height: $control-item-height;
			width: 100%;
			border-top: 1px solid var(--color-grey-850);
			border-bottom: 1px solid var(--color-grey-850);
		}
	}
}

</style>
